import React from "react";
import {Alert, Button, Modal, Popconfirm, Progress, Select, Space, Table, Tag, Tooltip, Typography, message} from "antd";
import {
    CloudDownloadOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    FileZipOutlined,
} from "@ant-design/icons";
import moment from "moment";
import i18n from "../../../services/i18n";
import {useIsSuperuser} from "../../../helpers/useHighestRole";
import type {ListYearEndExportsQuery, GetYearEndExportDownloadUrlQuery} from "../../../generated/graphql";
import {
    useListYearEndExportsQuery,
    useCreateYearEndExportMutation,
    useDeleteYearEndExportMutation,
    useGetYearEndExportDownloadUrlLazyQuery,
} from "../../../generated/graphql";

type YearEndExportItem = NonNullable<ListYearEndExportsQuery["listYearEndExports"]>[number];

/** Erstes Jahr, für das ein Export angeboten wird (siehe docs/plans/jahres-export.md). */
const FIRST_SELECTABLE_YEAR = 2015;

/** Formatiert eine Byte-Größe als lesbaren String (KB/MB/…). */
const formatBytes = (bytes: number): string => {
    if (!bytes) {
        return "0 Bytes";
    }
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Jahres-Export für KO-Superuser: Button in der Toolbar der Umsätze-Liste,
 * der ein Modal mit Jahr-Auswahl und der Liste bisheriger Exporte öffnet.
 *
 * Sichtbarkeit: ausschließlich `Roles.Superuser`. Ein SuperAdmin des Mandanten
 * sieht den Button nicht (`useIsSuperuser()` liefert während des Ladens der
 * Rollen ebenfalls `false`, deshalb genügt der eine Check).
 */
const YearEndExportModal: React.FC = () => {
    const isSuperuser = useIsSuperuser();

    // moment läuft dank TimezoneBootstrap in der Zeitzone des Mandanten — am
    // 31.12. abends ist das nicht zwingend das Jahr des Browsers.
    const currentYear = moment().year();

    const [open, setOpen] = React.useState<boolean>(false);
    const [selectedYear, setSelectedYear] = React.useState<number>(currentYear - 1);
    const [downloadingId, setDownloadingId] = React.useState<string | null>(null);

    const {data, loading, error: listError, startPolling, stopPolling} = useListYearEndExportsQuery({
        fetchPolicy: "network-only",
        skip: !open,
    });

    const exports = data?.listYearEndExports ?? [];

    // Polling nur solange ein Export läuft — und nur bei geöffnetem Modal.
    // Dependency ist `data` (stabile Referenz), nicht das bei jedem Render neu
    // erzeugte `exports`-Array.
    React.useEffect(() => {
        const hasCreating = (data?.listYearEndExports ?? []).some((item) => item.status === "creating");

        if (open && hasCreating) {
            startPolling(5000);
        } else {
            stopPolling();
        }

        return () => {
            stopPolling();
        };
    }, [open, data, startPolling, stopPolling]);

    const [createExport, {loading: createLoading}] = useCreateYearEndExportMutation({
        refetchQueries: ["ListYearEndExports"],
        onCompleted: () => {
            message.success(i18n.containers.payments.YearEndExport.successCreate());
        },
        onError: (error: {message: string}) => {
            message.error(error.message);
        },
    });

    const [deleteExport] = useDeleteYearEndExportMutation({
        refetchQueries: ["ListYearEndExports"],
        onCompleted: () => {
            message.success(i18n.containers.payments.YearEndExport.successDelete());
        },
        onError: (error: {message: string}) => {
            message.error(error.message);
        },
    });

    const [getDownloadUrl] = useGetYearEndExportDownloadUrlLazyQuery({
        fetchPolicy: "network-only",
        onCompleted: (urlData: GetYearEndExportDownloadUrlQuery) => {
            setDownloadingId(null);
            if (urlData?.getYearEndExportDownloadUrl) {
                window.open(urlData.getYearEndExportDownloadUrl, "_blank");
            }
        },
        onError: (error: {message: string}) => {
            setDownloadingId(null);
            message.error(error.message);
        },
    });

    const yearOptions = React.useMemo(() => {
        const years: Array<number> = [];
        for (let year = currentYear; year >= FIRST_SELECTABLE_YEAR; year--) {
            years.push(year);
        }

        return years;
    }, [currentYear]);

    const handleCreateExport = () => {
        // Jahr vorher auflösen: verschachtelte i18n-Aufrufe als Argument sind verboten.
        const yearText = String(selectedYear);

        Modal.confirm({
            title: i18n.containers.payments.YearEndExport.confirmCreateTitle(),
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>{i18n.containers.payments.YearEndExport.confirmCreateQuestion({year: yearText})}</p>
                    <p>{i18n.containers.payments.YearEndExport.confirmCreateHint()}</p>
                    <Alert
                        message={i18n.containers.payments.YearEndExport.alertSensitiveData()}
                        type="warning"
                        showIcon
                        style={{marginTop: 12}}
                    />
                </div>
            ),
            okText: i18n.containers.payments.YearEndExport.createExportButton(),
            cancelText: i18n.containers.payments.YearEndExport.no(),
            onOk: () => {
                createExport({variables: {year: selectedYear}});
            },
        });
    };

    const handleDownload = (exportId: string) => {
        setDownloadingId(exportId);
        getDownloadUrl({variables: {exportId}});
    };

    const handleDelete = (exportId: string) => {
        deleteExport({variables: {exportId}});
    };

    const renderStatus = (record: YearEndExportItem) => {
        if (record.status === "creating") {
            return (
                <Tooltip title={i18n.containers.payments.YearEndExport.statusCreating()}>
                    <Progress percent={record.progress || 0} size="small" status="active" style={{minWidth: 120}} />
                </Tooltip>
            );
        }

        if (record.status === "failed") {
            return (
                <Tooltip title={record.errorMessage || i18n.containers.payments.YearEndExport.statusFailed()}>
                    <Tag color="error">{i18n.containers.payments.YearEndExport.statusFailed()}</Tag>
                </Tooltip>
            );
        }

        if (record.status === "completed") {
            return <Tag color="success">{i18n.containers.payments.YearEndExport.statusCompleted()}</Tag>;
        }

        return <Tag>{record.status}</Tag>;
    };

    const columns = [
        {
            title: i18n.containers.payments.YearEndExport.columnCreatedAt(),
            dataIndex: "createdAt",
            key: "createdAt",
            width: 150,
            render: (date: string) => moment(date).format("DD.MM.YYYY HH:mm"),
        },
        {
            title: i18n.containers.payments.YearEndExport.columnYear(),
            dataIndex: "year",
            key: "year",
            width: 80,
        },
        {
            title: i18n.containers.payments.YearEndExport.columnStatus(),
            key: "status",
            width: 160,
            render: (_: unknown, record: YearEndExportItem) => renderStatus(record),
        },
        {
            title: i18n.containers.payments.YearEndExport.columnRecordCount(),
            dataIndex: "recordCount",
            key: "recordCount",
            width: 110,
            render: (count: number) => (count > 0 ? count.toLocaleString("de-DE") : "-"),
        },
        {
            title: i18n.containers.payments.YearEndExport.columnSize(),
            dataIndex: "totalSize",
            key: "totalSize",
            width: 110,
            render: (size: number, record: YearEndExportItem) =>
                record.status === "completed" ? formatBytes(size) : "-",
        },
        {
            title: i18n.containers.payments.YearEndExport.columnActions(),
            key: "actions",
            width: 180,
            render: (_: unknown, record: YearEndExportItem) => (
                <Space size="small">
                    <Button
                        size="small"
                        icon={<CloudDownloadOutlined />}
                        onClick={() => handleDownload(record.id)}
                        loading={downloadingId === record.id}
                        disabled={record.status !== "completed" || downloadingId !== null}
                    >
                        {i18n.containers.payments.YearEndExport.downloadButton()}
                    </Button>
                    <Popconfirm
                        title={i18n.containers.payments.YearEndExport.confirmDeleteTitle()}
                        onConfirm={() => handleDelete(record.id)}
                        okText={i18n.containers.payments.YearEndExport.yes()}
                        cancelText={i18n.containers.payments.YearEndExport.no()}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={record.status === "creating"} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (!isSuperuser) {
        return null;
    }

    return (
        <>
            <Tooltip title={i18n.containers.payments.YearEndExport.toolbarTooltip()}>
                <Button
                    size="small"
                    type="link"
                    style={{fontSize: 12}}
                    onClick={() => setOpen(true)}
                    data-testid="year-end-export-button"
                >
                    <Space size={5}>
                        <FileZipOutlined />
                        {i18n.containers.payments.YearEndExport.toolbarButton()}
                    </Space>
                </Button>
            </Tooltip>

            <Modal
                title={i18n.containers.payments.YearEndExport.modalTitle()}
                open={open}
                width={760}
                onCancel={() => setOpen(false)}
                footer={
                    <Button onClick={() => setOpen(false)}>
                        {i18n.containers.payments.YearEndExport.closeButton()}
                    </Button>
                }
            >
                <Alert
                    type="info"
                    showIcon
                    message={i18n.containers.payments.YearEndExport.alertTitle()}
                    description={
                        <>
                            <div>{i18n.containers.payments.YearEndExport.alertContent()}</div>
                            <div>{i18n.containers.payments.YearEndExport.alertSnapshotHint()}</div>
                        </>
                    }
                    style={{marginBottom: 16}}
                />

                <Space style={{marginBottom: 16}} size="middle">
                    <span>
                        <strong>{i18n.containers.payments.YearEndExport.labelYear()}:</strong>{" "}
                        <Select
                            value={selectedYear}
                            onChange={(value: number) => setSelectedYear(value)}
                            style={{width: 110}}
                            data-testid="year-end-export-year-select"
                        >
                            {yearOptions.map((year) => (
                                <Select.Option key={year} value={year}>
                                    {year}
                                </Select.Option>
                            ))}
                        </Select>
                    </span>
                    <Button type="primary" onClick={handleCreateExport} loading={createLoading}>
                        {i18n.containers.payments.YearEndExport.createExportButton()}
                    </Button>
                </Space>

                {listError && (
                    <Alert
                        type="error"
                        showIcon
                        style={{marginBottom: 12}}
                        message={i18n.containers.payments.YearEndExport.loadError()}
                        description={listError.message}
                    />
                )}

                <Table
                    dataSource={exports}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    size="small"
                    locale={{
                        emptyText: (
                            <Typography.Text type="secondary">
                                {i18n.containers.payments.YearEndExport.noExportsYet()}
                            </Typography.Text>
                        ),
                    }}
                    pagination={{
                        pageSize: 5,
                        hideOnSinglePage: true,
                        showTotal: (total: number) =>
                            i18n.containers.payments.YearEndExport.paginationTotal({total: String(total)}),
                    }}
                />
            </Modal>
        </>
    );
};

export default YearEndExportModal;
